import React, { useState } from 'react';
import {
    IconButton,
    Tooltip,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    SimpleGrid,
    Text,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Box,
} from '@chakra-ui/react';
import { EMOJI_CATEGORIES } from './emojiCategories';

/**
 * Standalone emoji-picker trigger button. Insertion (cursor position, maxLength
 * capping, refocusing the field) is the caller's job via `onSelect` — this
 * component only renders the picker UI, so it can sit next to any Input or
 * Textarea (WhatsApp templates, In-App Notification, Push Notification, ...).
 */
export default function EmojiPickerButton({ onSelect, isDisabled, size = 'xs', placement = 'top-end' }) {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = (emoji) => {
        onSelect(emoji);
        setIsOpen(false);
    };

    return (
        <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement={placement} isLazy>
            <PopoverTrigger>
                <Tooltip label="Emoji" hasArrow placement="top">
                    <IconButton
                        aria-label="Emoji"
                        size={size}
                        variant="ghost"
                        isDisabled={isDisabled}
                        onClick={() => setIsOpen((o) => !o)}
                        icon={<Text fontSize="sm">😊</Text>}
                    />
                </Tooltip>
            </PopoverTrigger>
            <PopoverContent w="300px" p={0} boxShadow="lg">
                <PopoverBody p={0}>
                    <Tabs size="sm" variant="unstyled" isLazy>
                        <TabList
                            overflowX="auto"
                            borderBottom="1px solid"
                            borderColor="gray.200"
                            px={1}
                            css={{
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': { display: 'none' },
                            }}
                        >
                            {EMOJI_CATEGORIES.map((cat) => (
                                <Tab
                                    key={cat.title}
                                    fontSize="16px"
                                    px={2}
                                    py={1}
                                    minW="auto"
                                    _selected={{ borderBottom: '2px solid', borderColor: 'green.400' }}
                                    title={cat.title}
                                >
                                    {cat.label}
                                </Tab>
                            ))}
                        </TabList>
                        <TabPanels>
                            {EMOJI_CATEGORIES.map((cat) => (
                                <TabPanel key={cat.title} p={2} maxH="200px" overflowY="auto">
                                    <SimpleGrid columns={10} spacing={1}>
                                        {cat.emojis.map((emoji, i) => (
                                            <Box
                                                key={i}
                                                as="button"
                                                fontSize="18px"
                                                lineHeight="1.4"
                                                p="2px"
                                                borderRadius="sm"
                                                cursor="pointer"
                                                _hover={{ bg: 'gray.100' }}
                                                onClick={() => handleSelect(emoji)}
                                                title={emoji}
                                            >
                                                {emoji}
                                            </Box>
                                        ))}
                                    </SimpleGrid>
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </Tabs>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}
